import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * A hook that allows calling a function after a state update and then resets the flag.
 * @param fn The function to call after the update
 * @returns A function that when called will trigger the passed function after the next render
 */
export const useFunctionAfterUpdate = <T extends (...args: any[]) => any>(
  fn: T
) => {
  const [shouldCall, setShouldCall] = useState(false);
  const [args, setArgs] = useState<Parameters<T> | null>(null);
  const wasCalledRef = useRef(false);

  useEffect(() => {
    if (shouldCall && fn && args && !wasCalledRef.current) {
      wasCalledRef.current = true;
      fn(...args);
      setShouldCall(false); // Reset the flag after calling the function
    } else if (!shouldCall) {
      // Reset the wasCalledRef when shouldCall becomes false
      wasCalledRef.current = false;
    }
  }, [shouldCall, fn, args]);

  const callAfterUpdate = useCallback((...callArgs: Parameters<T>) => {
    setArgs(callArgs);
    setShouldCall(true);
  }, []);

  return callAfterUpdate;
};
